// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";
import { ArrowPosition, ArrowStyleOverrides } from "@stoneage/App/Components/XngCarousel/models";
import CarouselLeftArrowIcon from "@stoneage/XDL/Icons/CarouselLeftArrowIcon";
import CarouselRightArrowIcon from "@stoneage/XDL/Icons/CarouselRightArrowIcon";
import ChevronLeft from "@stoneage/XDL/Icons/ChevronLeft";
import ChevronRight from "@stoneage/XDL/Icons/ChevronRight";

export const DEFAULT_ARROW_ICONS: Record<ArrowPosition, ArrowStyleOverrides> = {
    left: { src: CarouselLeftArrowIcon, size: 52, color: "common.white" },
    right: { src: CarouselRightArrowIcon, size: 52, color: "common.white" },
    top: { src: ChevronLeft, size: 12, color: "common.white" },
    bottom: { src: ChevronRight, size: 12, color: "common.white" },
};

interface ArrowStyleOptions {
    arrowPosition: ArrowPosition;
    showOnMobile?: boolean;
    showNavigation?: boolean;
    paginationOffset?: number;
    overrides?: ArrowStyleOverrides;
}

export const buildArrowStyle = ({
    arrowPosition,
    showOnMobile = false,
    showNavigation = false,
    paginationOffset = 0,
    overrides,
}: ArrowStyleOptions) => {
    const isVertical = arrowPosition === "top" || arrowPosition === "bottom";
    const mobileVisibility = showOnMobile
        ? css`
              &.arrow-on-desktop {
                  display: flex;
              }
          `
        : css`
              &.arrow-on-desktop {
                  display: none;
              }
          `;

    return {
        size: overrides?.size ?? 52,
        color: overrides?.color ?? "primary.main",
        css: css`
            &:not(.arrow-below-slides) {
                position: absolute;
                top: calc(50% - ${paginationOffset}px);
                transform: translateY(-50%);
            }

            aspect-ratio: 1;
            padding: 0;
            background: transparent;
            border: 0;
            outline: none !important;
            z-index: 430;

            display: flex;
            justify-content: center;
            align-items: center;

            &:focus {
                box-shadow: none;
            }

            & > ${IconWrapper} {
                transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
            }
            &:not(.arrow-below-slides) > ${IconWrapper} {
                color: ${color("primary.main")};
            }

            &:hover > ${IconWrapper} {
                color: ${color("common.ui1")};
            }

            /* Horizontal position */
            ${arrowPosition === "left" &&
            css`
                left: 0;
            `}
            ${arrowPosition === "right" &&
            css`
                right: 0;
            `}

            /* Vertical layout — arrow spans full width, rotated icon */
            ${isVertical &&
            css`
                position: absolute;
                inset: 0;
                transform: none;
                margin: 0;
                height: 28px;
                width: unset;

                ${IconWrapper} {
                    transform: rotate(90deg);
                }
            `}

            ${arrowPosition === "top" &&
            css`
                top: 0;
            `}
            ${arrowPosition === "bottom" &&
            css`
                top: calc(100% - 28px) !important;
            `}

            &.swiper-button-disabled {
                visibility: hidden;
            }

            ${!showNavigation &&
            css`
                display: none;
            `}

            ${xScreen("lte", "sm")} {
                ${mobileVisibility}
            }

            &.white-special-arrow {
                circle[fill-rule="evenodd"],
                path {
                    transition: all ease-in-out ${getProp("theme.transition.duration.regular", 300)}ms;
                }
                &:hover {
                    circle[fill-rule="evenodd"] {
                        fill: ${color("primary")};
                    }
                    path {
                        fill: ${color("common.white")};
                    }
                }
            }

            /* Consumer overrides — applied last so they always win */
            ${overrides?.css}
        `,
    };
};
